import React, { useState } from 'react';
import Modal from '../common/Modal';
import { parseFinancialValue } from '../../utils/numberUtils';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/formatters';
import { API_BASE_URL } from '../../config';
import { Eye, Download } from 'lucide-react';

function QuoteModal({ isOpen, onClose, quote, requisitionData, onEvaluate, isEvaluating, canEvaluate }) {
  const [evaluation, setEvaluation] = useState({
    notes: quote?.quotation_evaluation || '',
    selected: quote?.quotation_selected === '1' || false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEvaluation({
      ...evaluation,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onEvaluate(quote?.quotation_id, evaluation);
  };

  if (!quote) return null;
 

  // Parse quotation items from the quote data structure
  const parseQuotationItems = (quote, requisitionData) => {
    const items = [];
    
    // Get item descriptions from requisition data (already unserialized by backend)
    const itemDescriptions = requisitionData?.specification_items || quote.specification_items || quote.quotation_items || [];
    
    // Get quantities from requisition data (already unserialized by backend)
    const quantities = requisitionData?.specification_qty || quote.specification_qty || quote.quotation_qty || [];
    
    // Get amounts, units, comments, and vats from quote data
    const amounts = quote.quotation_amounts || [];
    const units = quote.quotation_units || [];
    const comments = quote.quotation_comments || [];
    const vats = quote.quotation_vats || [];
    
    // Ensure we have arrays to work with
    const itemArray = Array.isArray(itemDescriptions) ? itemDescriptions : (itemDescriptions ? [itemDescriptions] : []);
    const qtyArray = Array.isArray(quantities) ? quantities : (quantities ? [quantities] : []);
    const amountArray = Array.isArray(amounts) ? amounts : (amounts ? [amounts] : []);
    const unitArray = Array.isArray(units) ? units : (units ? [units] : []);
    const commentArray = Array.isArray(comments) ? comments : (comments ? [comments] : []);
    
    // Parse VATs - could be array, JSON string, or comma-separated string
    let vatArray = [];
    if (Array.isArray(vats)) {
      vatArray = vats;
    } else if (typeof vats === 'string' && vats !== '0' && vats !== '') {
      // First try JSON parse
      try {
        vatArray = JSON.parse(vats);
      } catch (e) {
        // If JSON parse fails, check if it's a comma-separated string
        if (vats.includes(',')) {
          vatArray = vats.split(',').map(v => v.trim());
        } else {
          // Single value
          vatArray = [vats];
        }
      }
    }
    
    // Build items array
    const maxLength = Math.max(itemArray.length, qtyArray.length, amountArray.length);
    
    for (let i = 0; i < maxLength; i++) {
      const description = itemArray[i] || `Item ${i + 1}`;
      const quantity = parseFloat(qtyArray[i]) || 1;
      const lineSubtotal = amountArray[i] || 0; // quotation_amounts contains line subtotals (qty × unit price)
      const comment = commentArray[i] || '';
      const vatAmount = vatArray[i] || 0;
      
      // Parse financial values
      const parsedLineSubtotal = parseFinancialValue(lineSubtotal);
      const parsedVatAmount = parseFinancialValue(vatAmount);
      
      // Calculate unit price from line subtotal ÷ quantity
      const parsedUnitPrice = quantity > 0 ? parsedLineSubtotal / quantity : 0;
      
      // Line total = line subtotal + VAT
      const lineTotal = parsedLineSubtotal + parsedVatAmount;
      
      items.push({
        id: i,
        description: description,
        quantity: quantity,
        unit_price: parsedUnitPrice,
        total: lineTotal,
        comment: comment,
        vat_amount: parsedVatAmount
      });
    }
    
    return items;
  };

  const quotationItems = parseQuotationItems(quote, requisitionData);

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={`Quote Details - ${quote.quotation_company_name || 'Supplier'}`}
      size="2xl"
    >
      <div className="max-h-[75vh] overflow-y-auto px-1">
        {/* Top row with three columns: Supplier Information, Attached Documents, Evaluation */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          {/* Supplier Information */}
          <div className="bg-white border border-gray-100 rounded-md p-3">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0 w-8 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2-1a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V4a1 1 0 00-1-1H6z" clipRule="evenodd" />
                  <path d="M8 7a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1zm0 4a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                </svg>
              </div>
              <h4 className="text-sm font-medium text-gray-700 ml-2">Supplier Information</h4>
            </div>
            <div className="space-y-3">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-8 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2-1a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V4a1 1 0 00-1-1H6z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900">{quote.quotation_company_name || 'Not specified'}</div>
                  <div className="text-xs text-gray-500">Company Name</div>
                </div>
              </div>

              {quote.quotation_company_person && (
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900">{quote.quotation_company_person}</div>
                    <div className="text-xs text-gray-500">Contact Person</div>
                  </div>
                </div>
              )}

              {quote.quotation_date && (
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900">{formatDateForDisplay(quote.quotation_date)}</div>
                    <div className="text-xs text-gray-500">Quote Date</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Attached Documents */}
          <div className="bg-white border border-gray-100 rounded-md p-3">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0 w-8 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h4 className="text-sm font-medium text-gray-700 ml-2">Attached Documents</h4>
            </div>
            
            <div className="space-y-2">
              {/* First check for new attachments (standardized) */}
              {quote.attachments && quote.attachments.length > 0 ? (
                quote.attachments.map((attachment, index) => {
                  const fileName = attachment.file_name || 'Unnamed Document';
                  const filePath = attachment.file_path;
                  const fileUrl = `${API_BASE_URL}/api/attachments/quotations/${filePath}`;
                  
                  return (
                    <div key={`attachment-${index}-${attachment.attachment_id}`} className="flex items-center p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors group">
                      <div className="flex-shrink-0 w-6 text-gray-400 mr-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate" title={fileName}>
                          {fileName}
                        </p>
                      </div>
                      <div className="flex space-x-1 ml-2">
                        <button
                          onClick={() => window.open(fileUrl + '?v=' + Date.now(), '_blank')}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="View"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = fileUrl + '?download=1';
                            link.download = fileName;
                            link.click();
                          }}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Download"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : null}
              
              {/* Then show legacy files array (using old download method) */}
              {quote.files && quote.files.length > 0 ? (
                quote.files.map((file, index) => {
                  const fileName = file.quotation_file_name || file.quotation_file || 'Unnamed Document';
                  // Construct proper file path: {quotation_directory}/{filename}
                  // Note: FILES constant already includes directory/project/ so we don't need to prepend 'directory/'
                  const filePath = quote.quotation_directory ? 
                    `${quote.quotation_directory}/${file.quotation_file}` : 
                    file.quotation_file;
                  
                  // Debug logging for file object
                  console.log('QuoteModal - File object:', file);
                  console.log('QuoteModal - File name:', fileName);
                  console.log('QuoteModal - Quotation directory:', quote.quotation_directory);
                  console.log('QuoteModal - Constructed file path:', filePath);
                  
                  return (
                    <div key={`file-${index}-${file.quotation_file_id || index}`} className="flex items-center p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors group">
                      <div className="flex-shrink-0 w-6 text-gray-400 mr-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate" title={fileName}>
                          {fileName} <span className="text-xs text-gray-400">(legacy)</span>
                        </p>
                      </div>
                      {filePath && (
                        <div className="flex space-x-1 ml-2">
                          <button
                            onClick={() => {
                              // Debug logging for download action
                              console.log('QuoteModal - Download clicked for file:', file);
                              console.log('QuoteModal - Using filePath:', filePath);
                              
                              // Use backend controller for forced download
                              const downloadUrl = `${API_BASE_URL}/files/storage?file=${encodeURIComponent(filePath)}`;
                              console.log('QuoteModal - Download URL:', downloadUrl);
                              window.open(downloadUrl, '_blank');
                            }}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="Download (Legacy)"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : null}
              
              {/* Fallback for single quotation_file field */}
              {!quote.attachments?.length && !quote.files?.length && quote.quotation_file ? (
                <div className="flex items-center p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors group">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900">Quotation Document <span className="text-xs text-gray-400">(legacy)</span></p>
                  </div>
                  <div className="flex space-x-1 ml-2">
                    <button
                      onClick={() => {
                        // Debug logging for single file download
                        console.log('QuoteModal - Single quotation_file download clicked');
                        console.log('QuoteModal - quote.quotation_file:', quote.quotation_file);
                        console.log('QuoteModal - quote.quotation_directory:', quote.quotation_directory);
                        
                        // Construct proper file path: {quotation_directory}/{filename}
                        // Note: FILES constant already includes directory/project/ so we don't need to prepend 'directory/'
                        const filePath = quote.quotation_directory ? 
                          `${quote.quotation_directory}/${quote.quotation_file}` : 
                          quote.quotation_file;
                        
                        console.log('QuoteModal - Constructed file path:', filePath);
                        
                        // Use backend controller for forced download
                        const downloadUrl = `${API_BASE_URL}/files/storage?file=${encodeURIComponent(filePath)}`;
                        console.log('QuoteModal - Single file download URL:', downloadUrl);
                        window.open(downloadUrl, '_blank');
                      }}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                      title="Download (Legacy)"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ) : null}
              
              {/* Show message if no files at all */}
              {!quote.attachments?.length && !quote.files?.length && !quote.quotation_file && (
                <div className="text-xs text-gray-500 italic">No documents attached</div>
              )}
            </div>
          </div>

          {/* Evaluation */}
          <div className="bg-white border border-gray-100 rounded-md p-3">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0 w-8 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h4 className="text-sm font-medium text-gray-700 ml-2">Evaluation</h4>
            </div>
            
            <div className="space-y-2">
              {quote.quotation_evaluation && quote.quotation_evaluation !== "" ? (
                (() => {
                  try {
                    // Parse evaluation data
                    let evalData;
                    
                    // Try to parse as JSON first
                    if (quote.quotation_evaluation.startsWith('[') || quote.quotation_evaluation.startsWith('{')) {
                      evalData = JSON.parse(quote.quotation_evaluation);
                    } else {
                      // If it's not JSON, it might be serialized PHP data - handle as array
                      console.warn('Non-JSON evaluation data detected:', quote.quotation_evaluation);
                      // For now, set default values
                      evalData = ['', '', '', ''];
                    }
                    
                    // Calculate total score
                    let evaluation = {};
                    if (Array.isArray(evalData)) {
                      evaluation = {
                        quality: evalData[0] || '',
                        adherence: evalData[1] || '',
                        delivery: evalData[2] || '',
                        price: evalData[3] || ''
                      };
                    } else if (typeof evalData === 'object') {
                      evaluation = {
                        quality: evalData.quality || '',
                        adherence: evalData.adherence || '',
                        delivery: evalData.delivery || '',
                        price: evalData.price || ''
                      };
                    }
                    
                    // Calculate total score
                    const totalScore = Object.values(evaluation).reduce((sum, val) => sum + (parseInt(val) || 0), 0);
                    
                    // Get badge for evaluation
                    let badgeText = '';
                    let badgeClass = '';
                    
                    if (totalScore >= 13) {
                      badgeText = 'Excellent';
                      badgeClass = 'bg-green-100 text-green-800';
                    } else if (totalScore >= 9) {
                      badgeText = 'Above Average';
                      badgeClass = 'bg-blue-100 text-blue-800';
                    } else if (totalScore >= 5) {
                      badgeText = 'Average';
                      badgeClass = 'bg-yellow-100 text-yellow-800';
                    } else {
                      badgeText = 'Poor';
                      badgeClass = 'bg-red-100 text-red-800';
                    }
                    
                    return (
                      <>
                        <div className="flex items-center mb-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
                            {totalScore}/16 - {badgeText}
                          </span>
                        </div>
                        
                        <div className="bg-gray-50 p-2 rounded-lg">
                          <div className="grid grid-cols-1 gap-1 text-xs">
                            <div className="flex justify-between py-0.5">
                              <span className="text-gray-600">Quality:</span>
                              <span className="font-medium">{evaluation.quality}/4</span>
                            </div>
                            <div className="flex justify-between py-0.5">
                              <span className="text-gray-600">Adherence:</span>
                              <span className="font-medium">{evaluation.adherence}/4</span>
                            </div>
                            <div className="flex justify-between py-0.5">
                              <span className="text-gray-600">Delivery:</span>
                              <span className="font-medium">{evaluation.delivery}/4</span>
                            </div>
                            <div className="flex justify-between py-0.5">
                              <span className="text-gray-600">Price:</span>
                              <span className="font-medium">{evaluation.price}/4</span>
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  } catch (error) {
                    console.error('Error parsing evaluation data:', error);
                    return <div className="text-xs text-red-500">Error displaying evaluation data</div>;
                  }
                })()
              ) : (
                <div className="text-xs text-gray-500 italic">No evaluation completed</div>
              )}
            </div>
          </div>
        </div>

        {/* Quote Items Table */}
        {quotationItems.length > 0 ? (
          <div className="bg-white overflow-hidden mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Quotation Line Items</h4>
            <div className="overflow-x-auto border border-gray-200 rounded-md">
              <table className="min-w-full divide-y divide-gray-200 table-fixed">
                <colgroup>
                  <col className="w-[40%]" /> {/* Item Description */}
                  <col className="w-[10%]" /> {/* Quantity */}
                  <col className="w-[17%]" /> {/* Unit Price */}
                  <col className="w-[17%]" /> {/* VAT */}
                  <col className="w-[16%]" /> {/* Total */}
                </colgroup>
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item Description
                    </th>
                    <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Unit Price
                    </th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      VAT
                    </th>
                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {quotationItems.map((item, index) => (
                    <tr key={index}>
                      <td className="px-3 py-2 text-sm text-gray-900">
                        <div className="font-medium">{item.description}</div>
                        {item.comment && (
                          <div className="text-xs text-gray-500 mt-1">{item.comment}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500 text-center">
                        {item.quantity}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900 text-right whitespace-nowrap">
                        R {formatCurrency(item.unit_price)}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500 text-right whitespace-nowrap">
                        {item.vat_amount > 0 ? `R ${formatCurrency(item.vat_amount)}` : '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900 text-right font-medium whitespace-nowrap">
                        R {formatCurrency(item.total)}
                      </td>
                    </tr>
                  ))}
                  
                  {/* Summary rows */}
                  <tr className="bg-gray-50 border-t-2 border-gray-300">
                    <td colSpan="4" className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                      Subtotal:
                    </td>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                      R {formatCurrency(parseFinancialValue(quote.quotation_total) - parseFinancialValue(quote.quotation_vat))}
                    </td>
                  </tr>
                  
                  {quote.quotation_vat && parseFinancialValue(quote.quotation_vat) > 0 ? (
                    <tr className="bg-gray-50">
                      <td colSpan="4" className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                        VAT (15%):
                      </td>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                        R {formatCurrency(parseFinancialValue(quote.quotation_vat))}
                      </td>
                    </tr>
                  ) : null}
                  
                  
                  <tr className="bg-blue-50 border-t-2 border-blue-300">
                    <td colSpan="4" className="px-3 py-2 text-sm font-bold text-blue-900 text-right">
                      Grand Total:
                    </td>
                    <td className="px-3 py-2 text-lg font-bold text-blue-600 text-right">
                      R {formatCurrency(parseFinancialValue(quote.quotation_total))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

      </div>
    </Modal>
  );
}

export default QuoteModal;
